import React, { FC, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDaysIcon, MapPinIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { DATE_TIME_COPY, locationLabel } from "@1-now/booking-core";
import { useData } from "data/data-provider";
import { usePastAnchor } from "../TopBarSearch/useTopBarSearchVisible";
import { LocationListingModal } from "models/LocationModal";
import CarTripCard from "./CarTripCard";
import DatesPickerB from "./DatesPickerB";
import LocationPickerB from "./LocationPickerB";
import PickerLayer from "./PickerLayer";
import TimePickerB from "./TimePickerB";
import { compactRange } from "./availability";
import { useIsSheet } from "./useIsSheet";

/** What the bar watches: the page's trip card (CarTripCard marks itself with it). */
export const TRIP_FIELDS_SELECTOR = "[data-car-trip-fields]";

export interface CarTripBarProps {
  /** The booking steps are open (the phone sheet, or the desktop pages): the bar stays away. */
  suppressed: boolean;
  carName?: string;
  selectedCar: any;
  ourLocation: LocationListingModal[];
  /** Refresh the price: the page's own quote. */
  onUpdate: () => unknown;
  isDropOffVisible: boolean;
  canChooseDropoff: boolean;
  onDifferentDropoff: () => void;
  onSameDropoff: () => void;
}

const updateClass =
  "inline-flex h-10 shrink-0 items-center justify-center rounded-lg bg-primary-brand px-5 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-700 disabled:opacity-60";

/**
 * 1NOW-11176: the car page's trip, pinned to the top once the page's own trip fields have scrolled away.
 *
 * It shows this car's trip (location · dates · time) with Update, over the site header. It edits the SAME
 * trip as the fields on the page (the same state, through the same pickers, so its calendar is this
 * car's availability too), stays on the car page, and Update asks the page for a fresh quote. Desktop: a
 * compact bar whose three parts each open their picker. Phones: one compact control that opens the trip
 * as a sheet. It never shows while the booking steps are open. Portalled to the body, like the home
 * page's pinned header, so no transformed ancestor can turn "fixed" into "fixed to that box".
 */
const CarTripBar: FC<CarTripBarProps> = ({
  suppressed,
  carName,
  selectedCar,
  ourLocation,
  onUpdate,
  isDropOffVisible,
  canChooseDropoff,
  onDifferentDropoff,
  onSameDropoff,
}) => {
  const { pickupLocation, startDate, endDate, ourCompany, searchedPickupLoction } = useData();
  const past = usePastAnchor(TRIP_FIELDS_SELECTOR, !suppressed);
  const phone = useIsSheet();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  if (suppressed || !past) return null;

  // Matches the site header it covers, so the page does not shift when the bar takes its place.
  const headerHeight = document.querySelector<HTMLElement>(".nc-Header")?.offsetHeight || undefined;

  const update = async () => {
    if (!pickupLocation) {
      toast.error("Please select pick-up location");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please pick your dates first");
      return;
    }
    setBusy(true);
    try {
      await onUpdate();
    } finally {
      setBusy(false);
      setSheetOpen(false);
    }
  };

  const where = searchedPickupLoction || (pickupLocation ? locationLabel(pickupLocation, ourCompany?.company_name) : "Select location");
  const when = startDate && endDate ? compactRange(startDate, endDate) : DATE_TIME_COPY.datePlaceholder;

  const updateButton = (
    <button type="button" onClick={update} disabled={busy} className={updateClass} data-testid="car-trip-update">
      {busy ? "Updating…" : "Update"}
    </button>
  );

  // The popovers hang under the bar, centred, not under the chip that opened them: a chip is narrow and
  // may sit near an edge, a calendar is wide.
  const under = (width: string) => `fixed left-1/2 top-[68px] -translate-x-1/2 ${width}`;

  return createPortal(
    <div
      data-testid="car-trip-bar"
      className="cpb fixed inset-x-0 top-0 z-[60] border-b border-text-color/10 bg-card-color text-text-color shadow-[0_2px_12px_rgba(0,0,0,0.08)]"
      style={{ minHeight: headerHeight }}
    >
      <div className="container flex items-center gap-2 py-2 md:gap-3">
        {phone ? (
          <>
            <button
              type="button"
              onClick={() => setSheetOpen(true)}
              aria-haspopup="dialog"
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-text-color/20 px-3 py-2.5 text-left text-sm font-medium"
            >
              <MapPinIcon className="h-4 w-4 shrink-0 text-text-color/60" />
              <span className="min-w-0 flex-1 truncate">{where}</span>
              <span className="h-4 w-px shrink-0 bg-text-color/15" aria-hidden="true" />
              <CalendarDaysIcon className="h-4 w-4 shrink-0 text-text-color/60" />
              <span className="max-w-[45%] shrink-0 truncate">{when}</span>
            </button>
            {updateButton}
            <div ref={sheetRef} />
            <PickerLayer
              open={sheetOpen}
              onClose={() => setSheetOpen(false)}
              title="Your trip"
              wrapperRef={sheetRef}
              name="trip-sheet"
              footer={
                <button
                  type="button"
                  onClick={update}
                  disabled={busy}
                  className="h-11 w-full rounded-lg bg-primary-brand text-sm font-semibold text-on-primary disabled:opacity-60"
                >
                  {busy ? "Updating…" : "Update price"}
                </button>
              }
            >
              <CarTripCard
                variant="sheet"
                ourLocation={ourLocation}
                selectedCar={selectedCar}
                isDropOffVisible={isDropOffVisible}
                canChooseDropoff={canChooseDropoff}
                onDifferentDropoff={onDifferentDropoff}
                onSameDropoff={onSameDropoff}
              />
            </PickerLayer>
          </>
        ) : (
          <>
            {carName && (
              <p className="hidden min-w-0 max-w-[240px] shrink truncate text-sm font-semibold lg:block">
                {carName}
              </p>
            )}
            <div className="flex min-w-0 flex-1 justify-center">
              <div className="flex min-w-0 items-stretch divide-x divide-text-color/15 overflow-visible rounded-lg border border-text-color/20">
                <div className="min-w-0 max-w-[260px]">
                  <LocationPickerB
                    kind="pickup"
                    variant="chip"
                    ourLocation={ourLocation}
                    separateDropoff={isDropOffVisible}
                    desktopClassName={under("w-[min(480px,calc(100vw-2rem))]")}
                  />
                </div>
                <DatesPickerB variant="chip" selectedCar={selectedCar} />
                <TimePickerB variant="chip" desktopClassName={under("w-[min(560px,calc(100vw-2rem))]")} />
              </div>
            </div>
            {updateButton}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default CarTripBar;
